import axios from "axios";
import Response from "../models/response";
import config from "./config";
import { refreshAccessToken } from "./httpClient";

export const authConfigHeader = (token) => ({
  headers: { Authorization: `Bearer ${token}` },
});
export const appJsonContentTypeHeader = () => ({
  headers: { "Content-Type": "application/json" },
});
export const addHeaders = (...headerObjects) => {
  let newHeader = {};
  const headers = headerObjects.map((hObject) => hObject.headers);
  Object.assign(newHeader, ...headers);

  return { headers: newHeader };
};

export async function authenticate(email, password) {
  let response = await axios
    .post(
      `${config.base_url}/auth`,
      { email: email, password: password },
      { skipAuthRefresh: true },
    )
    .then((res) => new Response(res.status, res.data.message, res.data.data))
    .catch((err) => {
      const status = err?.response?.status;
      const message = err?.response?.data?.message || err?.message || "";

      if (status === 401) {
        return new Response(401, message || "Wrong credentials entered!");
      }

      if (
        status === 403 ||
        /confirm|verif|not\s*activ|email.*not/i.test(String(message))
      ) {
        return new Response(
          status || 403,
          message || "Email not confirmed",
          err?.response?.data?.data,
        );
      }

      return new Response(
        status || 500,
        message || "Internal server error!",
      );
    });

  return response;
}

/** True when login failed because email is not confirmed yet. */
export const isEmailNotConfirmedError = (response) => {
  if (!response) return false;
  const status = response.statusCode ?? response.status;
  const message = String(response.message || "").toLowerCase();

  if (status === 403) return true;
  return /confirm|verif|not\s*activ|email.*not|nije\s*potvr/.test(message);
};

export async function decodeToken(jwt) {
  let response = await axios
    .post(
      `${config.base_url}/auth/decode`,
      JSON.stringify(jwt),
      appJsonContentTypeHeader()
    )
    .then((res) => res.data.data)
    .catch((err) => {
      err = err.response;
      return new Response(err.status, err.data.message);
    });

  return response;
}

export async function getRefreshedToken() {
  return refreshAccessToken();
}

export async function isApiWorking() {
  return axios
    .get(`${config.base_url}/Home/test`)
    .then((res) => res.status === 200)
    .catch((err) => {
      console.log(err);
      return false;
    });
}

export async function register(newUser) {
  return axios
    .post(
      `${config.base_url}/auth/register`,
      newUser,
      { ...appJsonContentTypeHeader(), skipAuthRefresh: true },
    )
    .then((res) => {
      if (res.status === 201 || res.status === 200) {
        return new Response(
          res.status,
          res.data?.message || "Successful registration!",
          res.data?.data,
        );
      }
      return new Response(
        res.status,
        res.data?.message || "Registration failed!",
      );
    })
    .catch((err) => {
      const status = err?.response?.status || 500;
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Registration failed!";
      return new Response(status, message);
    });
}

export async function confirmEmail(userId, securityToken) {
  let data = { userId, securityToken };
  return axios
    .post(
      `${config.base_url}/auth/email/confirmation`,
      data,
      appJsonContentTypeHeader()
    )
    .then((res) => {
      switch (res.status) {
        case 200:
          return new Response(
            res.status,
            "Email successfully confirmed!",
            res.data.data
          );
        case 400:
          return new Response(res.status, "URL invalid!");
        case 500:
          return new Response(res.status, "Internal server error!");
        default:
          return new Response(res.status, "Status code not recognized!");
      }
    })
    .catch((err) => {
      console.log(err);
      return false;
    });
}

export async function sendRecoveryEmail(email) {
  return axios
    .post(
      `${config.base_url}/auth/password-reset/send-email`,
      JSON.stringify(email),
      appJsonContentTypeHeader()
    )
    .then((res) => {
      switch (res.status) {
        case 200:
          return new Response(res.status, res.data.message);
        case 400:
          return new Response(res.status, res.data.message);
        case 500:
          return new Response(res.status, "Internal server error!");
        default:
          return new Response(res.status, "Status code not recognized!");
      }
    })
    .catch((err) => {
      err = err.response;
      return new Response(err.status, err.data.message);
    });
}

export async function sendRecoveryEmailForgot(email) {
  return axios
    .post(
      `${config.base_url}/auth/forgot-password/send-email`,
      JSON.stringify(email),
      appJsonContentTypeHeader()
    )
    .then((res) => {
      switch (res.status) {
        case 200:
          return new Response(res.status, res.data.message);
        case 400:
          return new Response(res.status, res.data.message);
        case 500:
          return new Response(res.status, "Internal server error!");
        default:
          return new Response(res.status, "Status code not recognized!");
      }
    })
    .catch((err) => {
      err = err.response;
      return new Response(err.status, err.data.message);
    });
}

export async function resetPassword(
  userId,
  securityToken,
  newPassword,
  oldPassword
) {
  let data = { userId, securityToken, newPassword, oldPassword };
  return axios
    .put(
      `${config.base_url}/auth/password-reset/reset`,
      data,
      appJsonContentTypeHeader()
    )
    .then((res) => {
      switch (res.status) {
        case 200:
          return new Response(res.status, "Your password is changed!");
        case 400:
          return new Response(res.status, res.data.message);
        case 500:
          return new Response(res.status, "Internal server error!");
        default:
          return new Response(res.status, "Status code not recognized!");
      }
    })
    .catch((err) => {
      err = err.response;
      return new Response(err.status, err.data.message);
    });
}

export async function forgotPassword(userId, securityToken, newPassword) {
  let data = { userId, securityToken, newPassword };
  return axios
    .put(
      `${config.base_url}/auth/forgot-password/reset`,
      data,
      appJsonContentTypeHeader()
    )
    .then((res) => {
      switch (res.status) {
        case 200:
          return new Response(res.status, "Your password is changed!");
        case 400:
          return new Response(res.status, res.data.message);
        case 500:
          return new Response(res.status, "Internal server error!");
        default:
          return new Response(res.status, "Status code not recognized!");
      }
    })
    .catch((err) => {
      err = err.response;
      return new Response(err.status, err.data.message);
    });
}

export async function editUserInfo(
  jwt,
  firstName,
  lastName,
  title,
  academicTitle,
  jobTitle,
  jobField,
  phoneNumber
) {
  let data = {
    jwt,
    firstName,
    lastName,
    title,
    academicTitle,
    jobTitle,
    jobField,
    phoneNumber,
  };
  let headers = addHeaders(
    appJsonContentTypeHeader(),
    authConfigHeader(localStorage.getItem("jwt"))
  );
  return axios
    .put(`${config.base_url}/users/edit`, data, headers)
    .then((res) => {
      return res.status === 200
        ? new Response(res.status, "Successful registration!", res.data.data)
        : new Response(res.status, "Registration failed!");
    })
    .catch((err) => {
      err = err.response;
      return new Response(err.status, err.data.message);
    });
}
